function normalizeNumberString(v) {
  const raw = String(v ?? '').trim();
  if (!raw) return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return '';
  return String(n);
}

/**
 * Parse a PID UPC weight string.
 *
 * Supports:
 * - "5 CT"
 * - "4 lb"
 * - "5 CT, 4 lb"
 * - "5 CT / 4 lb"
 * - "4 lb, 5 CT" (will still parse; storage will re-order)
 */
export function parseWeightParts(input) {
  const raw = String(input ?? '').trim();
  const out = { count: '', pounds: '' };
  if (!raw) return out;

  const re = /(\d+(?:\.\d+)?)\s*(lb|ct)\b/ig;
  for (const m of raw.matchAll(re)) {
    const num = m[1];
    const unit = String(m[2] || '').toLowerCase();
    if (unit === 'ct' && !out.count) out.count = num;
    if (unit === 'lb' && !out.pounds) out.pounds = num;
  }

  // Legacy tolerance: if no unit is present but it's numeric, treat as pounds.
  if (!out.count && !out.pounds) {
    const n = Number.parseFloat(raw);
    if (Number.isFinite(n)) out.pounds = String(n);
  }

  return out;
}

/**
 * Build canonical backend storage string.
 *
 * Rules:
 * - Count is always first when both are present.
 * - Stored dual format uses comma: "5 CT, 4 lb"
 */
export function buildWeightStorageString({ count, pounds }) {
  const ct = normalizeNumberString(count);
  const lb = normalizeNumberString(pounds);

  const parts = [];
  if (ct) parts.push(`${ct} CT`);
  if (lb) parts.push(`${lb} lb`);
  return parts.join(', ');
}

/**
 * Frontend display string.
 *
 * Rules:
 * - Display dual format uses slash: "5 CT / 4 lb"
 * - Single format stays as "5 CT" or "4 lb"
 */
export function formatWeightForDisplay(input) {
  const { count, pounds } = parseWeightParts(input);
  const ct = normalizeNumberString(count);
  const lb = normalizeNumberString(pounds);

  if (ct && lb) return `${ct} CT / ${lb} lb`;
  if (ct) return `${ct} CT`;
  if (lb) return `${lb} lb`;
  return '';
}

/**
 * Metric conversion suffix for the pounds portion (display only).
 *
 * Examples:
 * - "4 lb" -> " (1.81 KG)"
 * - "0.5 lb" -> " (227 g)"
 * - "5 CT" -> ""
 */
export function formatMetricSuffixFromPounds(input) {
  const { pounds } = parseWeightParts(input);
  const lbStr = normalizeNumberString(pounds);
  if (!lbStr) return '';

  const lb = Number(lbStr);
  if (!Number.isFinite(lb)) return '';

  const kg = lb * 0.45359237;
  if (kg >= 1) return ` (${kg.toFixed(2)} KG)`;

  const g = Math.round(kg * 1000);
  return ` (${g} g)`;
}

